import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type RequestDetail } from '../api';
import { Alert, Badge, Button, Spinner } from '../components/ui';
import { IconSearch } from '../components/icons';

/**
 * Points this offboarding at the enrolment it reverses.
 *
 * The reason this exists rather than trusting memory: whoever raises an
 * offboarding is usually recalling what somebody was given three years ago,
 * and what gets forgotten is precisely what stays switched on. Linking pulls
 * the actual granted list across, defaulting to revoke.
 *
 * Optional throughout. Plenty of leavers predate this system, and refusing to
 * offboard somebody because there is no enrolment on file would be absurd.
 */
export function EnrolmentPicker({
  request,
  onLinked,
}: {
  request: RequestDetail;
  /** Linking rewrites the request server-side, and the wizard owns a working
   *  copy that a refetch deliberately will not disturb, so hand it the new
   *  version rather than hoping the cache reaches it. */
  onLinked: (request: RequestDetail) => void;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(request.employee?.fullName ?? '');

  const matches = useQuery({
    queryKey: ['enrolments', search],
    queryFn: () => api.enrolments(search),
    enabled: open,
  });

  const link = useMutation({
    mutationFn: (enrolmentId: string | null) => api.linkEnrolment(request.id, enrolmentId),
    onSuccess: (result) => {
      setOpen(false);
      onLinked(result.request);
      void queryClient.invalidateQueries({ queryKey: ['request', request.id] });
    },
  });

  if (request.supersedes) {
    return (
      <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-brand-200 bg-brand-50/60 px-4 py-3 dark:border-brand-900 dark:bg-brand-950/30">
        <Badge tone="brand">Linked</Badge>
        <span className="min-w-0 flex-1 text-sm text-slate-700 dark:text-slate-300">
          Reversing <span className="font-medium">{request.supersedes.reference}</span>
          {request.supersedes.completedAt &&
            `, completed ${new Date(request.supersedes.completedAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}`}
          . What it granted has been carried across.
        </span>
        <Button size="sm" variant="ghost" disabled={link.isPending} onClick={() => link.mutate(null)}>
          Unlink
        </Button>
      </div>
    );
  }

  return (
    <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3 dark:border-slate-800 dark:bg-slate-900/40">
      {!open ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="min-w-0 flex-1 text-sm text-slate-700 dark:text-slate-300">
            Is there an enrolment for this person? Linking it fills in their details and lists what they were
            actually given, so nothing is missed.
          </span>
          <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
            <IconSearch className="h-4 w-4" />
            Find enrolment
          </Button>
        </div>
      ) : (
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              autoFocus
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name…"
              aria-label="Search completed enrolments by employee name"
              className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            />
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>

          {link.isError && (
            <div className="mt-3">
              <Alert>{link.error instanceof ApiError ? link.error.message : 'Could not link that enrolment.'}</Alert>
            </div>
          )}

          {matches.isPending ? (
            <Spinner label="Searching" />
          ) : (matches.data?.enrolments.length ?? 0) === 0 ? (
            <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
              No completed enrolment matches that. Carry on and fill the form in by hand. Plenty of people
              predate this system.
            </p>
          ) : (
            <ul className="mt-3 divide-y divide-slate-200 dark:divide-slate-800">
              {matches.data!.enrolments.map((option) => (
                <li key={option.id} className="flex flex-wrap items-center gap-3 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900 dark:text-white">
                      {option.employeeName ?? 'Unnamed'}{' '}
                      <span className="font-normal text-slate-500 dark:text-slate-400">{option.reference}</span>
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {[option.jobTitle, option.department].filter(Boolean).join(' · ') || 'No details recorded'} ·{' '}
                      {option.itemCount} item{option.itemCount === 1 ? '' : 's'}
                      {option.completedAt &&
                        ` · completed ${new Date(option.completedAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}`}
                    </p>
                  </div>
                  <Button size="sm" disabled={link.isPending} onClick={() => link.mutate(option.id)}>
                    {link.isPending ? 'Linking…' : 'Use this'}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
